// Sidebar Navigation Component
// =============================
// Persistent desktop sidebar for navigation
// Feature #133: Added accessibility improvements (role attributes, aria-labels)
// Feature #244: Removed 'Aktuelt forløb' widget for cleaner design
// Feature #136: Re-added 'Aktuelt forløb' widget with interactive progress and quick-start
// Feature #292: Updated with new design language - subtle backgrounds, refined typography

import React, { useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLearningJourneys } from '../../hooks/useLearningJourneys';
import { useJourneyTopics } from '../../hooks/useJourneyTopics';
import { LyraLogo, DesignLine } from '../ui';
import type { LearningJourney } from '../../types';

// Helper function to calculate days until exam - Feature #136
const getDaysUntilExam = (examDate: Date | string | undefined): number => {
  if (!examDate) return -1;
  const exam = examDate instanceof Date ? examDate : new Date(examDate);
  exam.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffTime = exam.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const navKeys = [
  { path: '/dashboard', labelKey: 'nav.dashboard', icon: '🏠' },
  { path: '/sessions', labelKey: 'nav.myJourneys', icon: '📚' },
  { path: '/preparation', labelKey: 'nav.preparation', icon: '📝' },
  { path: '/settings', labelKey: 'nav.settings', icon: '⚙️' },
];

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { user, logout } = useAuth();

  // Feature #136: Use centralized learning journeys hook for current journey widget
  const { journeys } = useLearningJourneys();

  // Feature #136: Compute the most relevant journey (closest upcoming exam)
  const currentJourneyId = useMemo<string | undefined>(() => {
    if (journeys.length === 0) return undefined;

    // Find the journey with the closest upcoming exam date
    const upcomingJourneys = journeys
      .filter((j) => {
        const days = getDaysUntilExam(j.examDate);
        return days >= 0; // Only future exams
      })
      .sort((a, b) => {
        const daysA = getDaysUntilExam(a.examDate);
        const daysB = getDaysUntilExam(b.examDate);
        return daysA - daysB; // Closest first
      });

    if (upcomingJourneys.length > 0) {
      return upcomingJourneys[0].id;
    }

    // If no upcoming exams, show the most recent journey
    const sorted = [...journeys].sort(
      (a, b) =>
        new Date(b.createdAt || '').getTime() -
        new Date(a.createdAt || '').getTime()
    );
    return sorted[0]?.id;
  }, [journeys]);

  // Feature #136: Get topics for current journey to calculate progress
  const { topics } = useJourneyTopics(currentJourneyId);

  // Feature #136: Get the current journey object from the computed ID
  const currentJourney = useMemo<LearningJourney | null>(() => {
    if (!currentJourneyId) return null;
    return journeys.find((j) => j.id === currentJourneyId) || null;
  }, [journeys, currentJourneyId]);

  // Feature #136: Calculate progress based on completed subtopics
  const progressData = useMemo(() => {
    if (topics.length === 0) {
      return { completedCount: 0, totalCount: 0, progressPercent: 0 };
    }

    let completed = 0;
    let total = 0;

    topics.forEach((topic) => {
      if (topic.subtopics && topic.subtopics.length > 0) {
        // Count subtopics
        topic.subtopics.forEach((subtopic) => {
          total++;
          if (subtopic.isCompleted || subtopic.completed) {
            completed++;
          }
        });
      } else {
        // If no subtopics, count the topic itself
        total++;
        if (topic.completedSessions && topic.completedSessions > 0) {
          completed++;
        }
      }
    });

    const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completedCount: completed, totalCount: total, progressPercent: percent };
  }, [topics]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Feature #136: Calculate days until exam for display
  const daysUntilExam = currentJourney
    ? getDaysUntilExam(currentJourney.examDate)
    : -1;

  // Persist last active workspace for quick re-entry
  const lastActiveWorkspace = useMemo(() => {
    try {
      const stored = localStorage.getItem('lyra_lastActiveWorkspace');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const continueTarget = lastActiveWorkspace?.sessionId
    ? `/session/${lastActiveWorkspace.sessionId}`
    : lastActiveWorkspace
      ? `/workspace/${lastActiveWorkspace.journeyId}/${lastActiveWorkspace.topicId}`
      : `/sessions`;

  return (
    <aside
      className="hidden md:flex md:flex-col md:w-64 md:fixed md:inset-y-0 bg-canvas dark:bg-canvas-dark border-r border-frame-border dark:border-frame-border-dark z-30"
      aria-label={t('sidebar.mainNav')}
    >
      {/* Logo - Feature #155: SVG logo, Feature #292: Refined styling with DesignLine */}
      <div className="flex flex-col h-16 border-b border-frame-border dark:border-frame-border-dark">
        <div className="flex items-center flex-1 px-6">
          <NavLink
            to="/dashboard"
            className="flex items-center gap-2"
            aria-label={t('sidebar.goToDashboard')}
          >
            <LyraLogo size="md" showText={true} />
          </NavLink>
        </div>
        {/* Feature #292: DesignLine accent under logo */}
        <DesignLine variant="top-accent" />
      </div>

      {/* Navigation Links - Feature #292: Refined active/hover states */}
      <nav
        role="navigation"
        aria-label={t('accessibility.sidebarNav')}
        className="flex-1 px-3 py-5 space-y-1 overflow-y-auto"
      >
        {navKeys.map((item) => {
          const label = t(item.labelKey);
          return (
            <NavLink
              key={item.path}
              to={item.path}
              aria-label={label}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-sans font-medium transition-all duration-200 ${
                  isActive
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 shadow-sm border-l-2 border-design-line-blue'
                    : 'text-neutral-medium hover:bg-background-light dark:hover:bg-background-dark hover:text-neutral-dark dark:hover:text-white'
                }`
              }
            >
              <span className="text-xl" aria-hidden="true">
                {item.icon}
              </span>
              <span>{label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* Feature #136: Aktuelt forløb widget - Interactive with progress and quick-start */}
      {/* Feature #292: Updated styling with subtle backgrounds and refined typography */}
      {currentJourney && (
        <div className="px-3 pb-4">
          <div
            onClick={() => navigate(`/journeys/${currentJourneyId}`)}
            className="w-full p-4 bg-background-light dark:bg-background-dark border border-frame-border dark:border-frame-border-dark rounded-lg text-left cursor-pointer hover:border-primary-200 dark:hover:border-primary-700 transition-all duration-200 group"
            role="button"
            tabIndex={0}
            aria-label={`${t('sidebar.currentJourney')}: ${currentJourney.subject}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(`/journeys/${currentJourneyId}`);
              }
            }}
          >
            <p className="text-xs text-neutral-medium mb-1 uppercase tracking-wide font-sans font-medium">
              {t('sidebar.currentJourney')}
            </p>
            <p className="text-sm font-semibold font-sans text-neutral-dark dark:text-white truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
              {currentJourney.subject}
            </p>

            {/* Progress bar - Feature #136 step 4, Feature #292: Refined styling */}
            {progressData.totalCount > 0 && (
              <>
                <div className="mt-3 h-1 bg-frame-border dark:bg-frame-border-dark rounded-full overflow-hidden">
                  <div
                    className="h-full bg-design-line-blue transition-all duration-300"
                    style={{ width: `${progressData.progressPercent}%` }}
                  />
                </div>
                <p className="text-xs text-neutral-medium mt-1 font-sans">
                  {t('sidebar.topicsProgress', { completed: progressData.completedCount, total: progressData.totalCount })}
                </p>
              </>
            )}

            {/* Days until exam - Feature #292: Refined typography */}
            {currentJourney.examDate && daysUntilExam >= 0 && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-lg" aria-hidden="true">
                  📅
                </span>
                <span
                  className={`text-sm font-medium font-sans ${
                    daysUntilExam <= 7
                      ? 'text-design-line-orange'
                      : 'text-neutral-medium'
                  }`}
                >
                  {daysUntilExam === 0
                    ? t('sidebar.examToday')
                    : t('sidebar.daysLeft', { count: daysUntilExam })}
                </span>
              </div>
            )}

            {/* Topic name + session type */}
            {lastActiveWorkspace?.topicTitle && (
              <p className="mt-2 text-xs text-neutral-medium font-sans truncate">
                {lastActiveWorkspace.sessionType === 'introduction' && '👋 '}
                {lastActiveWorkspace.sessionType === 'dialog' && '💬 '}
                {lastActiveWorkspace.sessionType === 'exam' && '🎯 '}
                {lastActiveWorkspace.topicTitle}
              </p>
            )}

            {/* Continue button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(continueTarget);
              }}
              aria-label={t('sidebar.continue')}
              className="mt-3 w-full text-xs bg-primary-50 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 py-2 rounded-md hover:bg-primary-100 dark:hover:bg-primary-900/60 transition-all duration-200 font-sans font-medium border border-transparent hover:border-primary-200 dark:hover:border-primary-700"
            >
              {t('sidebar.continue')}
            </button>
          </div>
        </div>
      )}

      {/* Keyboard Shortcut Hint - Feature #292: Updated styling */}
      <div className="px-3 py-2 border-t border-frame-border dark:border-frame-border-dark">
        <p className="text-xs text-neutral-medium text-center font-sans">
          {t('sidebar.shortcutsPrefix')} <kbd className="px-1.5 py-0.5 mx-0.5 text-xs font-mono bg-background-light dark:bg-background-dark border border-frame-border dark:border-frame-border-dark rounded">
            ?
          </kbd> {t('sidebar.shortcutsSuffix')}
        </p>
      </div>

      {/* User Info and Logout - Feature #292: Updated styling */}
      <div className="px-3 py-4 border-t border-frame-border dark:border-frame-border-dark">
        <div className="flex items-center gap-3">
          {/* User Avatar - Feature #292: Refined avatar styling */}
          <div className="w-10 h-10 rounded-lg bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0 border border-frame-border dark:border-frame-border-dark">
            <span className="text-lg font-sans font-semibold text-primary-600 dark:text-primary-400">
              {user?.displayName?.charAt(0)?.toUpperCase() ||
                user?.email?.charAt(0)?.toUpperCase() ||
                '?'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-sans font-medium text-neutral-dark dark:text-white truncate">
              {user?.displayName || t('sidebar.user')}
            </p>
            <p className="text-xs text-neutral-medium font-sans truncate">
              {user?.email}
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 text-neutral-medium hover:text-design-line-orange hover:bg-background-light dark:hover:bg-background-dark rounded-lg transition-all duration-200 flex-shrink-0"
            title={t('actions.logOut')}
            aria-label={t('actions.logOut')}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
